import { my_routes } from "../router/all_routes";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { createTeacher } from "../../api/teachers";
import { toast } from "sonner";
import CommonSelect from "../../core/common/commonSelect";

import { useAcademicYear } from "../../core/common/context/AcademicYearContext";
import InfosAlertYearDisabled from "../../core/common/alerts/infosAlertYearDisabled";

const status = [
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" },
];

const sexe = [
  { value: "Masculin", label: "Masculin" },
  { value: "Feminin", label: "Feminin" },
];

// shéma du formulaire pour enregistré l'inscription d'un professeur
const teacherShema = z.object({
  lastname: z.string().nonempty("Veuillez insérer un nom"),
  firstname: z.string().nonempty("Veuillez insérer un prénom"),
  sexe: z
    .object(
      {
        value: z.string(),
        label: z.string(),
      },
      {
        required_error: "Veuillez choisir un genre valide",
        invalid_type_error: "Veuillez choisir un genre valide",
      },
    )
    .refine(
      (val) => val && val.value && val.label,
      "veuillez choisir un genre valide",
    ),
  birthday: z.any().optional(),
  email: z
    .string()
    // .email("Veuillez fournir une adresse email valide")
    .optional(),
  number: z
    .string()
    // .regex(
    //   /^(\+223)?[456789]\d{7}$/,
    //   "Numéro de téléphone invalide. Ex: +22370123456 ou 70123456"
    // )
    .optional(),
  adresse: z.string().optional(),
  speciality: z.string().optional(),
  statut: z.enum(["true", "false"], {
    required_error: "Veuillez sélectionner un statut",
  }),
});
type RegisterFormFields = z.infer<typeof teacherShema>;

const CreateTeacher = () => {
  const { id } = useParams(); // On récupère l'id de l'URL
  const routes = my_routes;
  const { selectedYear } = useAcademicYear();

  const navigate = useNavigate();
  const location = useLocation();

  //   Initialisation de zod avec react-hook-form pour le compte parent
  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormFields>({
    resolver: zodResolver(teacherShema),
    defaultValues: {
      statut: "true",
    },
  });

  // Fonction de soumission du formulaire d'inscription
  const onSubmitRegister: SubmitHandler<RegisterFormFields> = async (data) => {
    try {
      const formData = {
        ...data,
        statut: data.statut === "true",
        email: data.email === "" ? undefined : data.email,
        sexe: data.sexe.value,
      };
      const response = await createTeacher(formData);
      toast.success("Professeur créé avec succès");
      navigate(routes.allTeacher);
    } catch (error: any) {
      // Gérer les erreurs de l'API
      // console.log("erreur edit prof: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };
  return (
    <>
      {/* Page wrapper */}
      <div className="page-wrapper">
        <div className="content content-two">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="mb-1">Ajouter Professeur</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.allTeacher}>Professeurs</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Ajouter Professeur
                  </li>
                </ol>
              </nav>
            </div>
          </div>
          {/* /Page Header */}
          {!selectedYear?.statut && <InfosAlertYearDisabled />}
          <div className="row">
            <div className="col-md-12">
              <form onSubmit={handleSubmit(onSubmitRegister)}>
                {/* Information Personnel */}
                <div className="card">
                  <div className="card-header bg-light ">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-info-square-rounded fs-16" />
                      </span>
                      <h4 className="text-dark">Informations Personnel</h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    <div className="row">
                      <div className="col-md-12">
                        <div className="d-flex align-items-center flex-wrap row-gap-3 mb-3">
                          <div className="d-flex align-items-center justify-content-center avatar avatar-xxl border border-dashed me-2 flex-shrink-0 text-dark frames">
                            <i className="ti ti-photo-plus fs-16" />
                          </div>
                          <div className="profile-upload">
                            <div className="profile-uploader d-flex align-items-center">
                              <div className="drag-upload-btn mb-3">
                                Upload
                                <input
                                  type="file"
                                  className="form-control image-sign"
                                  multiple
                                />
                              </div>
                              <Link to="#" className="btn btn-primary mb-3">
                                Remove
                              </Link>
                            </div>
                            <p className="fs-12">
                              Upload image size 4MB, Format JPG, PNG, SVG
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row row-cols-xxl-5 row-cols-md-6">
                      {/* <div className="col-xxl col-xl-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Teacher ID</label>
                            <input
                              type="text"
                              className="form-control"
                              defaultValue={isEdit ? "T849126" : undefined}
                            />
                          </div>
                        </div> */}
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Prénom</label>
                          <input
                            type="text"
                            placeholder="Entrer un prénom"
                            className={
                              errors.firstname
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            defaultValue={undefined}
                            {...register("firstname")}
                          />
                          {errors.firstname && (
                            <div className="invalid-feedback">
                              {errors.firstname.message}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Nom</label>
                          <input
                            type="text"
                            placeholder="Entrer un nom"
                            className={
                              errors.lastname
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            defaultValue={undefined}
                            {...register("lastname")}
                          />
                          {errors.lastname && (
                            <div className="invalid-feedback">
                              {errors.lastname.message}
                            </div>
                          )}
                        </div>
                      </div>
                      {/* <div className="col-xxl col-xl-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Class</label>
                            <CommonSelect
                              className="select"
                              options={allClass}
                              defaultValue={isEdit ? allClass[0] : undefined}
                            />
                          </div>
                        </div> */}
                      {/* <div className="col-xxl col-xl-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Subject</label>
                            <CommonSelect
                              className="select"
                              options={allSubject}
                              defaultValue={isEdit ? allSubject[0] : undefined}
                            />
                          </div>
                        </div> */}
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Genre</label>
                          <CommonSelect
                            className={
                              errors.sexe || errors.sexe
                                ? "select is-invalid"
                                : "select"
                            }
                            onChange={(option: any) => {
                              setValue("sexe", option || "");
                              clearErrors("sexe");
                            }}
                            options={sexe}
                            defaultValue={undefined}
                          />
                          {errors.sexe && (
                            <div className="invalid-feedback">
                              {errors.sexe.message}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Numéro Téléphone</label>
                          <input
                            type="text"
                            placeholder="Ex. +223 843820012"
                            className={
                              errors.number
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            defaultValue={undefined}
                            {...register("number")}
                          />
                          {errors.number && (
                            <div className="invalid-feedback">
                              {errors.number.message}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Adresse Mail</label>
                          <input
                            type="email"
                            placeholder="Ex. example@gmail.com"
                            className={
                              errors.email
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            defaultValue={undefined}
                            {...register("email")}
                          />
                          {errors.email && (
                            <div className="invalid-feedback">
                              {errors.email.message}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Date de Naissance
                          </label>
                          <div className="input-icon position-relative">
                            <input
                              type="date"
                              className={
                                errors.birthday
                                  ? "form-control datetimepicker is-invalid"
                                  : "form-control datetimepicker"
                              }
                              placeholder="DD-MM-YYYY"
                              {...register("birthday")}
                            />

                            {errors.birthday && (
                              <div className="invalid-feedback">
                                {errors.birthday &&
                                typeof errors.birthday.message === "string"
                                  ? errors.birthday.message
                                  : null}
                              </div>
                            )}
                            {/* <span className="input-icon-addon">
                                <i className="ti ti-calendar" />
                              </span> */}
                          </div>
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Specialite</label>
                          <input
                            type="text"
                            placeholder="Ex. Mathématique"
                            className={
                              errors.speciality
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            defaultValue={undefined}
                            {...register("speciality")}
                          />
                          {errors.speciality && (
                            <div className="invalid-feedback">
                              {errors.speciality.message}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-xxl-3 col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Status</label>
                          <CommonSelect
                            className={
                              errors.statut || errors.statut
                                ? "select is-invalid"
                                : "select"
                            }
                            options={status}
                            defaultValue={status[0]}
                            onChange={(option: any) => {
                              setValue("statut", option?.value || "");
                              clearErrors("statut");
                            }}
                          />
                          {errors.statut && (
                            <div className="invalid-feedback">
                              {errors.statut.message}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-xxl-12 col-md-12 col-xl-12">
                        <div className="mb-3">
                          <label className="form-label">Adresse</label>
                          <textarea
                            placeholder="Exemple : Badalabougou, Commune 5, Rue 300 Porte 218"
                            rows={4}
                            cols={8}
                            className={
                              errors.adresse
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            defaultValue={undefined}
                            {...register("adresse")}
                          />
                          {errors.adresse && (
                            <div className="invalid-feedback">
                              {errors.adresse.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* /Information Personnel */}

                <div className="text-end">
                  <Link
                    to={routes.allTeacher}
                    type="button"
                    className="btn btn-light me-3"
                  >
                    Annuler
                  </Link>
                  {isSubmitting ? (
                    <button
                      className="btn btn-primary-light"
                      type="button"
                      disabled={true}
                    >
                      <span
                        className="spinner-border spinner-border-sm align-middle me-1 "
                        role="status"
                        aria-hidden="true"
                      />
                      Loading...
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="btn btn-primary"
                      // data-bs-dismiss="modal"
                    >
                      Ajouter Professeur
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {/* /Page wrapper */}
    </>
  );
};

export default CreateTeacher;
